// Copyright Materialize, Inc. and contributors. All rights reserved.
//
// Use of this software is governed by the Business Source License
// included in the LICENSE file.
//
// As of the Change Date specified in that file, in accordance with
// the Business Source License, use of this software will be governed
// by the Apache License, Version 2.0.

import {
  Avatar as ChakraAvatar,
  ButtonProps,
  HStack,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  Spinner,
  Tag,
  Text,
  useBreakpointValue,
  useTheme,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { Link as RouterLink } from "react-router-dom";

import { useSegment } from "~/analytics/segment";
import { apiClient } from "~/api/apiClient";
import { getCurrentTenant, useCurrentOrganization } from "~/api/auth";
import { logout, logoutAndRedirectOrThrow } from "~/api/materialize/auth";
import ThemeSwitcher from "~/components/ThemeSwitcher";
import UiPreviewSwitcher from "~/components/UiPreviewSwitcher";
import { AppConfigSwitch } from "~/config/AppConfigSwitch";
import {
  AdminPortal,
  AuthActions,
  AuthState,
  type User,
} from "~/external-library-wrappers/frontegg";
import { type AuthContextProps } from "~/external-library-wrappers/oidc";
import { SUPPORT_CHAT_URL, SUPPORT_URL } from "~/externalUrls";
import { AUTH_ROUTES } from "~/fronteggRoutes";
import { useSelfManagedProfile } from "~/hooks/useSelfManagedProfile";
import { NAV_HORIZONTAL_SPACING, NAV_HOVER_STYLES } from "~/layouts/constants";
import docUrls from "~/mz-doc-urls.json";
import { MaterializeTheme } from "~/theme";

const UserInfoHeaderBlock = ({
  name,
  email,
  sqlRole,
}: {
  name?: string;
  email?: string;
  sqlRole?: string;
}) => {
  const { colors } = useTheme<MaterializeTheme>();
  if (!name && !email && !sqlRole) return null;
  return (
    <VStack px="3" pt="3" pb="2" align="left" lineHeight="1.3" spacing="0">
      {name && <Text fontWeight="semibold">{name}</Text>}
      {email && (
        <Text mt="1" fontSize="xs" color={colors.foreground.secondary}>
          {email}
        </Text>
      )}
      {sqlRole && (
        <Text mt="1" fontSize="xs" color={colors.foreground.secondary}>
          SQL role: {sqlRole}
        </Text>
      )}
    </VStack>
  );
};

const SelfManagedUserInfoMenuItem = ({ auth }: { auth?: AuthContextProps }) => {
  const { name, email, sqlRole, isLoading } = useSelfManagedProfile(auth);
  if (isLoading && !name && !email && !sqlRole) {
    return (
      <>
        <HStack px="3" pt="3" pb="2">
          <Spinner size="sm" />
        </HStack>
        <MenuDivider />
      </>
    );
  }
  if (!name && !email && !sqlRole) return null;
  return (
    <>
      <UserInfoHeaderBlock name={name} email={email} sqlRole={sqlRole} />
      <MenuDivider />
    </>
  );
};

const UserInfoMenuItem = () => {
  return (
    <AppConfigSwitch
      cloudConfigElement={({ runtimeConfig }) => {
        if (runtimeConfig.isImpersonating) return null;
        const { user, auth, authActions } = runtimeConfig;
        return (
          <>
            <UserInfoHeaderBlock name={user?.name} email={user?.email} />
            <MenuDivider />
            <OrganizationMenuGroup
              user={user}
              authState={auth}
              authActions={authActions}
            />
            <MenuDivider />
          </>
        );
      }}
      selfManagedConfigElement={({ appConfig, runtimeConfig }) => {
        if (appConfig.authMode === "None") return null;
        return (
          <SelfManagedUserInfoMenuItem
            auth={
              runtimeConfig.isOidcAvailable ? runtimeConfig.auth : undefined
            }
          />
        );
      }}
    />
  );
};

const PricingMenuItem = () => (
  <MenuItem
    as={RouterLink}
    to="https://materialize.com/s/pricing"
    fontWeight="medium"
    target="_blank"
    title="See Materialize pricing"
  >
    Pricing
  </MenuItem>
);

const SelfManagedMenuButtonLabel = ({ auth }: { auth?: AuthContextProps }) => {
  const { colors } = useTheme<MaterializeTheme>();
  const { name } = useSelfManagedProfile(auth);
  return (
    <Text
      textStyle="text-ui-med"
      minW="100%"
      maxW="160px"
      noOfLines={1}
      textAlign="left"
      color={colors.foreground.primary}
    >
      {name ?? "Settings"}
    </Text>
  );
};

const OidcSignOutMenuItem = ({ auth }: { auth: AuthContextProps }) => {
  const handleLogout = async () => {
    // Clear both the session cookie and OIDC state so that
    // logout works regardless of which method the user used.
    if (apiClient.type === "self-managed") {
      try {
        await logout({ apiClient });
      } catch {
        // Cookie may not exist if user logged in via OIDC only.
      }
    }
    await auth.signoutRedirect();
  };

  return (
    <>
      <MenuDivider />
      <MenuItem fontWeight="medium" onClick={handleLogout}>
        Sign out
      </MenuItem>
    </>
  );
};

const SignOutMenuItem = () => {
  return (
    <AppConfigSwitch
      cloudConfigElement={({ runtimeConfig: { isImpersonating } }) => {
        if (isImpersonating) return null;
        return (
          <>
            <MenuDivider />
            <MenuItem
              fontWeight="medium"
              as={RouterLink}
              to={AUTH_ROUTES.logoutPath}
            >
              Sign out
            </MenuItem>
          </>
        );
      }}
      selfManagedConfigElement={({ appConfig, runtimeConfig }) => {
        if (appConfig.authMode === "None") return null;
        if (runtimeConfig.isOidcAvailable) {
          return <OidcSignOutMenuItem auth={runtimeConfig.auth} />;
        }
        return (
          <>
            <MenuDivider />
            <MenuItem fontWeight="medium" onClick={logoutAndRedirectOrThrow}>
              Sign out
            </MenuItem>
          </>
        );
      }}
    />
  );
};

const DefaultAvatar = () => {
  return <ChakraAvatar size="xs" />;
};

const SelfManagedAvatar = ({ auth }: { auth?: AuthContextProps }) => {
  const { name, picture } = useSelfManagedProfile(auth);
  return <ChakraAvatar size="xs" src={picture} name={name} />;
};

const Avatar = () => {
  return (
    <AppConfigSwitch
      cloudConfigElement={({ runtimeConfig }) => {
        if (runtimeConfig.isImpersonating) {
          return <DefaultAvatar />;
        }
        const { user } = runtimeConfig;
        return (
          <ChakraAvatar
            size="xs"
            src={user.profilePictureUrl ?? undefined}
            name={user.name}
          />
        );
      }}
      selfManagedConfigElement={({ appConfig, runtimeConfig }) => {
        if (appConfig.authMode === "None") return <DefaultAvatar />;
        return (
          <SelfManagedAvatar
            auth={
              runtimeConfig.isOidcAvailable ? runtimeConfig.auth : undefined
            }
          />
        );
      }}
    />
  );
};

const OrganizationName = () => {
  const { colors } = useTheme<MaterializeTheme>();

  const { organization } = useCurrentOrganization();

  if (!organization) {
    return null;
  }

  return (
    <Text
      textStyle="text-small"
      fontWeight="500"
      color={colors.foreground.secondary}
      minW="100%"
      maxW="160px"
      textAlign="left"
      noOfLines={1}
      // noOfLines sets display, so this has to come after it
      display={{ base: "none", lg: "-webkit-box" }}
    >
      {organization.name}
    </Text>
  );
};

const ProfileDropdown = ({
  isCollapsed,
  ...props
}: ButtonProps & { isCollapsed: boolean }) => {
  const { colors } = useTheme<MaterializeTheme>();
  const menuPlacement = useBreakpointValue({
    base: "bottom" as const,
    lg: "top-end" as const,
  });

  return (
    <Menu gutter={1} placement={menuPlacement}>
      <MenuButton
        aria-label="Profile"
        title="Profile"
        px={NAV_HORIZONTAL_SPACING}
        py={1}
        borderRadius={{ base: "lg", lg: "sm" }}
        _hover={{ lg: NAV_HOVER_STYLES }}
        _active={NAV_HOVER_STYLES}
        borderWidth={{ base: "1px", lg: "0" }}
        borderColor={colors.border.secondary}
        {...props}
      >
        <HStack spacing={2}>
          <Avatar />
          {!isCollapsed && (
            <VStack alignItems="flex-start" spacing={0} width="100%">
              <AppConfigSwitch
                cloudConfigElement={({ runtimeConfig }) => {
                  const userName = runtimeConfig.isImpersonating
                    ? "Impersonation user"
                    : runtimeConfig.user.name;
                  return (
                    <>
                      <Text
                        textStyle="text-ui-med"
                        minW="100%"
                        maxW="160px"
                        noOfLines={1}
                        textAlign="left"
                        color={colors.foreground.primary}
                      >
                        {userName}
                      </Text>
                      <OrganizationName />
                    </>
                  );
                }}
                selfManagedConfigElement={({ appConfig, runtimeConfig }) => {
                  if (appConfig.authMode === "None") {
                    return (
                      <Text
                        textStyle="text-ui-med"
                        color={colors.foreground.primary}
                      >
                        Settings
                      </Text>
                    );
                  }
                  return (
                    <SelfManagedMenuButtonLabel
                      auth={
                        runtimeConfig.isOidcAvailable
                          ? runtimeConfig.auth
                          : undefined
                      }
                    />
                  );
                }}
              />
            </VStack>
          )}
        </HStack>
      </MenuButton>
      <MenuList pb={2}>
        <UserInfoMenuItem />
        <ThemeSwitcher />
        <UiPreviewSwitcher />
        <AppConfigSwitch
          cloudConfigElement={({ runtimeConfig }) => {
            if (runtimeConfig.isImpersonating) return null;
            return (
              <>
                <MenuDivider />
                <ProfileMenuItems />
              </>
            );
          }}
        />
        <MenuDivider />
        <CommunityMenuItems />
        <SignOutMenuItem />
      </MenuList>
    </Menu>
  );
};

const OrganizationMenuGroup = ({
  user,
  authState,
  authActions,
}: {
  user: User;
  authState: AuthState;
  authActions: AuthActions;
}) => {
  const { tenantsState } = authState;
  const { switchTenant } = authActions;
  const tenantSwitchingEnabled = tenantsState.tenants.length > 1;
  const currentTenant = getCurrentTenant(user, tenantsState.tenants);

  const handleTenantClick = async (tenantId: string) => {
    if (tenantSwitchingEnabled && tenantId !== currentTenant?.tenantId) {
      await switchTenant({ tenantId });
      // We want to force a full page reload to clear the query params and reset all in-memory JS state
      // after the tenant switch.
      // eslint-disable-next-line react-hooks/immutability
      window.location.href = window.location.href.split("?")[0];
    }
  };

  return (
    <MenuGroup title="Organization">
      {tenantsState.tenants
        .filter((tenant) => tenant && tenant.name)
        .sort((t1, t2) =>
          // always show orgs in the same order
          t1.name.toLowerCase() < t2.name.toLowerCase() ? -1 : 1,
        )
        .map((tenant) => (
          <MenuItem
            key={`org-${tenant.tenantId}`}
            isDisabled={
              !tenantSwitchingEnabled ||
              currentTenant?.tenantId === tenant.tenantId
            }
            title={
              tenantSwitchingEnabled &&
              currentTenant?.tenantId !== tenant.tenantId
                ? "Set as active organization"
                : "Current organization"
            }
            justifyContent="space-between"
            gap="var(--ck-space-2)"
            _disabled={{
              opacity: 1,
              cursor: "default",
              background: "none",
            }}
            _active={{
              // clicking flashes a background when disabled without this
              background: "none",
            }}
            _hover={{
              background:
                currentTenant?.tenantId === tenant.tenantId
                  ? "none"
                  : "default",
              cursor:
                currentTenant?.tenantId === tenant.tenantId
                  ? "default"
                  : "pointer",
            }}
            onClick={() => handleTenantClick(tenant.tenantId)}
          >
            {tenant.name}{" "}
            {currentTenant?.tenantId === tenant.tenantId && (
              <Tag size="sm" colorScheme="lavender">
                active
              </Tag>
            )}
          </MenuItem>
        ))}
    </MenuGroup>
  );
};

export const ProfileMenuItems = () => {
  return (
    <VStack spacing={0} width="100%">
      <MenuItem fontWeight="medium" onClick={() => AdminPortal.show()}>
        Account settings
      </MenuItem>
      <PricingMenuItem />
    </VStack>
  );
};

const CommunityMenuItems = () => {
  const { track } = useSegment();

  return (
    <VStack spacing={0} width="100%">
      <MenuItem
        as={RouterLink}
        to={docUrls["/docs/"]}
        onClick={() => {
          track("Link Click", {
            label: "Docs",
            href: docUrls["/docs/"],
          });
        }}
        fontWeight="medium"
        target="_blank"
        title="View Materialize documentation"
      >
        Documentation
      </MenuItem>
      <MenuItem
        as={RouterLink}
        to={SUPPORT_CHAT_URL}
        fontWeight="medium"
        target="_blank"
      >
        Join us on Slack
      </MenuItem>
      <MenuItem
        as={RouterLink}
        to={SUPPORT_URL}
        fontWeight="medium"
        target="_blank"
      >
        Support
      </MenuItem>
    </VStack>
  );
};

export default ProfileDropdown;
